// Field editor modal — feels like editing a Notion property. Persists via PUT
// /api/firm-config.

import { useEffect, useState } from "react";
import { useAppData } from "../store";
import {
  Btn, Chip, IconBtn, TextArea, TextInput, Toggle,
} from "../components/Primitives";
import { Select } from "../components/Select";
import { Ic } from "../components/Icon";

interface Props { id: string; onClose: () => void; }

const TYPES = [
  { value: "Text",      label: "Text",      hint: "free form" },
  { value: "Number",    label: "Number",    hint: "e.g. $2.5M" },
  { value: "Yes / no",  label: "Yes / no",  hint: "boolean" },
  { value: "Enum",      label: "Enum",      hint: "pick from a list" },
  { value: "List",      label: "List",      hint: "multiple values" },
];

const SOURCES = ["Email", "Email + enrichment", "LinkedIn", "Enrichment only"];

export function FieldEditModal({ id, onClose }: Props) {
  const { data, saveConfig } = useAppData();
  const fields = data?.config.fields ?? [];

  const isNew = id === "new";
  const existing = !isNew ? fields.find((f) => ((f as any).key ?? f.id) === id) : undefined;

  const [name, setName] = useState((existing as any)?.key ?? existing?.label ?? "");
  const [type, setType] = useState(existing?.type ?? "Text");
  const [desc, setDesc] = useState(existing?.desc ?? "");
  const [req,  setReq]  = useState(existing?.required ?? true);
  const [src,  setSrc]  = useState(existing?.source ?? "Email");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const save = async () => {
    if (!data || !name.trim()) return;
    setSaving(true);
    try {
      const payload = {
        key: name.trim(), label: name.trim(), type,
        required: req, source: src, desc,
      };
      const next = isNew
        ? [...fields, payload as any]
        : fields.map((f) => ((f as any).key ?? f.id) === id ? { ...f, ...payload } : f);
      await saveConfig({ ...data.config, fields: next });
      onClose();
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!data || isNew) return;
    if (!confirm("Delete this field?")) return;
    await saveConfig({
      ...data.config,
      fields: fields.filter((f) => ((f as any).key ?? f.id) !== id),
    });
    onClose();
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}
           style={{ width: 640, maxWidth: "94vw" }}>
        <div className="modal-head">
          <div className="who">
            <div className="t">{isNew ? "New field" : "Edit field"}</div>
            <div className="s">A clear description matters most — it's the prompt Prism gives the model.</div>
          </div>
          <IconBtn ico="x" onClick={onClose} label="Close" />
        </div>
        <div className="modal-body">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 200px", gap: 14 }}>
            <div>
              <label className="field-label">Name</label>
              <TextInput value={name} onChange={setName} />
            </div>
            <div>
              <label className="field-label">Type</label>
              <Select value={type} onChange={setType} options={TYPES} style={{ width: "100%" }} />
            </div>
          </div>
          <div>
            <label className="field-label">Description</label>
            <TextArea
              rows={5} value={desc} onChange={setDesc}
              placeholder="Explain to the model what to look for and where to find it."
            />
            <div style={{
              color: "var(--ink-3)", fontSize: 11, marginTop: 6,
              display: "flex", alignItems: "center", gap: 6,
            }}>
              <Ic name="info" size={11} />
              Tip: write it the way you'd explain it to a sharp new associate.
            </div>
          </div>
          <div style={{ display: "flex", gap: 28 }}>
            <div>
              <label className="field-label">Required</label>
              <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                <Toggle on={req} onChange={setReq} />
                <span style={{ fontSize: 13, color: "var(--ink-2)" }}>
                  {req ? "Yes — extract on every inbound" : "Optional"}
                </span>
              </div>
            </div>
            <div style={{ flex: 1 }}>
              <label className="field-label">Source preference</label>
              <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                {SOURCES.map((s) => (
                  <Chip key={s} on={src === s} onClick={() => setSrc(s)}>{s}</Chip>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="modal-foot">
          {!isNew && <Btn kind="ghost" size="sm" onClick={remove}>Delete</Btn>}
          <span className="grow" />
          <Btn kind="ghost" onClick={onClose}>Cancel</Btn>
          <Btn kind="primary" onClick={save} disabled={saving || !name.trim()}>
            {saving ? "Saving…" : "Save field"}
          </Btn>
        </div>
      </div>
    </div>
  );
}
